import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useAuth } from '@/features/auth/AuthProvider'
import { fetchAccount, type Account } from '@/features/auth/account'
import { onboardingPath, signInPath, useCurrentPath } from '@/features/auth/guards'
import { errorMessage } from '@/lib/errors'
import { qk } from '@/lib/query-keys'
import { supabase } from '@/lib/supabase'
import { ConsentDialog, type ConsentKind } from './ConsentDialog'

/**
 * O que a ação exige:
 * - member: conta com cadastro concluído e não suspensa (ex.: denunciar);
 * - vote: + consentimento para votos e apoios (votar, marcar "útil", apoiar sugestão);
 * - publish: + consentimento de publicação (comentar, sugerir, enviar proposta).
 */
export type ParticipationNeed = 'member' | 'vote' | 'publish'

type ParticipationContextValue = {
  /**
   * Garante as condições para participar. Leva para o login ou para o cadastro
   * quando preciso e abre o pedido de consentimento. Resolve `true` se a ação
   * pode prosseguir agora.
   */
  ensure: (need: ParticipationNeed) => Promise<boolean>
}

const ParticipationContext = createContext<ParticipationContextValue | null>(null)

type PendingConsent = { kind: ConsentKind; resolve: (granted: boolean) => void }

export function ParticipationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const current = useCurrentPath()
  const queryClient = useQueryClient()
  const [pending, setPending] = useState<PendingConsent | null>(null)

  const ensure = useCallback(
    async (need: ParticipationNeed) => {
      // Enquanto a sessão salva no navegador ainda carrega, `user` pode estar vazio.
      const sessionUser = user ?? (await supabase.auth.getSession()).data.session?.user ?? null
      if (!sessionUser) {
        navigate(signInPath(current))
        return false
      }
      let account: Account
      try {
        account =
          queryClient.getQueryData<Account>(qk.me.account) ??
          (await queryClient.fetchQuery({ queryKey: qk.me.account, queryFn: fetchAccount }))
      } catch (error) {
        toast.error(errorMessage(error))
        return false
      }
      if (!account.onboarded) {
        navigate(onboardingPath(current))
        return false
      }
      if (account.suspended) {
        toast.error('Sua conta está suspensa. Veja os detalhes e como recorrer na página da sua conta.')
        return false
      }
      if (need === 'vote' && !account.settings.votes_consent_at) {
        return new Promise<boolean>((resolve) => setPending({ kind: 'vote', resolve }))
      }
      if (need === 'publish' && !account.settings.publication_consent_at) {
        return new Promise<boolean>((resolve) => setPending({ kind: 'publish', resolve }))
      }
      return true
    },
    [user, navigate, current, queryClient],
  )

  const value = useMemo(() => ({ ensure }), [ensure])

  return (
    <ParticipationContext.Provider value={value}>
      {children}
      <ConsentDialog
        kind={pending?.kind ?? null}
        onClose={(granted) => {
          pending?.resolve(granted)
          setPending(null)
        }}
      />
    </ParticipationContext.Provider>
  )
}

export function useParticipation(): ParticipationContextValue {
  const context = useContext(ParticipationContext)
  if (!context) throw new Error('useParticipation precisa estar dentro de <ParticipationProvider>.')
  return context
}
